import { colors, intl, ui, UserId, userApi, UserProfile } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'
import ProfileAvatar from 'users/profile-avatar'
import {
	ParticipantGroup,
	ParticipantStatus,
} from '../../model/participant-groups'

const HEADING_KEY: Record<ParticipantStatus, string> = {
	finished: 'knowledge_check.stage.group.finished',
	started: 'knowledge_check.stage.group.started',
	notStarted: 'knowledge_check.stage.group.not_started',
}

/** What the ring of an avatar says at a glance; the heading says it in words. */
const RING_COLOR: Record<ParticipantStatus, string> = {
	finished: colors.greenishCyan,
	started: colors.gold,
	notStarted: colors.borderGray,
}

const Group = styled.section`
	& + & {
		margin-top: 32px;
	}
`

const Heading = styled.h3`
	align-items: baseline;
	color: ${colors.blackish};
	display: flex;
	font-size: 20px;
	font-weight: 700;
	justify-content: space-between;
	line-height: 1.2;
	margin: 0 0 16px;

	${ui.responsive.desktopFHD} {
		font-size: 24px;
	}
`

const Count = styled.span`
	font-size: 24px;

	${ui.responsive.desktopFHD} {
		font-size: 32px;
	}
`

const Avatars = styled.ul`
	display: flex;
	flex-wrap: wrap;
	gap: 14px;
	list-style: none;
	margin: 0;
	padding: 4px;
`

const Participant = styled.li<{ status: ParticipantStatus }>`
	border-radius: 50%;
	box-shadow: 0 0 0 3px ${props => RING_COLOR[props.status]};
	height: 48px;
	position: relative;
	width: 48px;

	${ui.responsive.desktopFHD} {
		height: 56px;
		width: 56px;
	}
`

const Avatar = styled(ProfileAvatar)`
	border-radius: 50%;
	height: 100%;
	width: 100%;
`

// Read by assistive tech, never shown: the avatar alone does not say who it is
const VisuallyHidden = styled.span`
	height: 1px;
	overflow: hidden;
	position: absolute;
	width: 1px;
	clip: rect(0 0 0 0);
	white-space: nowrap;
`

type Props = {
	groups: ParticipantGroup[]
	profiles: Record<UserId, UserProfile>
}

/**
 * Who is where in the check, a group per status: a heading with how many, and
 * a face for each. The same panel beside the progress and beside the review.
 */
const ParticipantsPanel: FC<Props> = ({ groups, profiles }) => {
	const t = intl.useTranslate()
	return (
		<>
			{groups.map(({ status, userIds }) => {
				const heading = t(HEADING_KEY[status])
				return (
					<Group key={status}>
						<Heading>
							{heading}
							<Count>{userIds.length}</Count>
						</Heading>
						{userIds.length > 0 ? (
							<Avatars aria-label={heading}>
								{userIds.map(id => {
									const profile = profiles[id]
									const name = profile ? userApi.getDisplayName(profile) : ''
									return (
										<Participant
											key={id}
											status={status}
											title={name || undefined}
										>
											<Avatar profile={profile} />
											{name ? <VisuallyHidden>{name}</VisuallyHidden> : null}
										</Participant>
									)
								})}
							</Avatars>
						) : null}
					</Group>
				)
			})}
		</>
	)
}

export default ParticipantsPanel
